"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { Search, Loader2 } from "lucide-react";
import { EMPTY_SEARCH_RESULT, normalizeSearchResult, type SearchResult } from "@/lib/search-rank";
import { storePagePath } from "@/lib/page-type";

interface GlobalSearchProps {
    variant?: "hero" | "header";
}

export default function GlobalSearch({ variant = "hero" }: GlobalSearchProps) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<SearchResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    // Debounce search. Each run owns an AbortController: a slower, older response can neither overwrite a newer
    // one nor reopen the list after the text was cleared.
    useEffect(() => {
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            if (query.length >= 2) {
                setLoading(true);
                try {
                    const res = await fetch(`/api/search?q=${encodeURIComponent(query)}`, { signal: controller.signal });
                    // A 500 (e.g. a cold database) answers {error}: treat it, and any other unexpected shape, as "no results".
                    const data: unknown = res.ok ? await res.json() : null;
                    if (controller.signal.aborted) return;
                    setResults(normalizeSearchResult(data, query));
                    setIsOpen(true);
                } catch (error) {
                    if (controller.signal.aborted) return;
                    console.error("Search failed", error);
                    setResults(EMPTY_SEARCH_RESULT);
                    setIsOpen(true);
                } finally {
                    if (!controller.signal.aborted) setLoading(false);
                }
            } else {
                setLoading(false);
                setResults(null);
                setIsOpen(false);
            }
        }, 300); // 300ms debounce

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [query]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const isHeader = variant === "header";

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && results && results.stores.length > 0) {
            window.location.href = storePagePath(results.stores[0]);
        }
    };

    return (
        <div ref={searchRef} role="search" className={`relative w-full z-40 ${isHeader ? 'max-w-md' : 'max-w-2xl mx-auto'}`}>
            <div className="relative">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") setIsOpen(false);
                        else handleKeyDown(e);
                    }}
                    onFocus={() => query.length >= 2 && setIsOpen(true)}
                    placeholder={isHeader ? "Search stores..." : "Search for stores (e.g. Hostinger, NordVPN)..."}
                    aria-label="Search stores"
                    autoComplete="off"
                    enterKeyHint="search"
                    className={isHeader
                        ? "w-full rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00ff9d]/50 focus:border-[#00ff9d] backdrop-blur-md transition-all shadow-xl px-4 py-2 pl-10 text-sm"
                        : "cs-search__input"}
                />
                <div className={`absolute top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none ${isHeader ? 'left-3' : 'left-5'}`}>
                    {loading ? <Loader2 className={`animate-spin ${isHeader ? 'w-4 h-4' : 'w-5 h-5'}`} aria-hidden="true" /> : <Search className={`${isHeader ? 'w-4 h-4' : 'w-5 h-5'}`} aria-hidden="true" />}
                </div>
            </div>

            {/* Dropdown Results */}
            {isOpen && results && (
                <div className="cs-search__results absolute top-full left-0 right-0 mt-2 overflow-hidden max-h-[60vh] overflow-y-auto text-left">

                    {/* Stores Section */}
                    {results.stores.length > 0 && (
                        <div className="p-2">

                            {results.stores.map((store) => (
                                <Link
                                    key={store.id}
                                    href={storePagePath(store)}
                                    onClick={() => setIsOpen(false)}
                                    title={`${store.name} Coupons`}
                                    className="flex items-center gap-4 p-3 hover:bg-white/5 rounded-xl transition-colors group"
                                >
                                    <img src={store.logo} alt={`${store.name} logo`} className="w-10 h-10 object-contain bg-white rounded-lg p-1.5" />
                                    <div>
                                        <div className="font-bold text-white group-hover:text-[#00ff9d] transition-colors">{store.name}</div>
                                        <div className="text-xs text-gray-400">View Coupons</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}

                    {/* No Results */}
                    {results.stores.length === 0 && (
                        <div className="p-8 text-center text-gray-400">
                            No results found for "{query}"
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
